/** Sanitize proposal ref and build the browser/download PDF document title. */
export function sanitizeProposalPdfDocumentTitle(proposalRef: string): string {
  const cleaned = proposalRef
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  const ref = cleaned || "proposal";
  if (/^clarity\s*-/i.test(ref)) return ref;
  return `CLARITY - ${ref}`;
}

/** Filename including extension, e.g. `CLARITY - CLARI-2607019P-SO-R3.pdf`. */
export function proposalPdfDownloadFilename(proposalRef: string | null | undefined): string {
  return `${sanitizeProposalPdfDocumentTitle(proposalRef ?? "proposal")}.pdf`;
}

/** RFC 5987-friendly Content-Disposition filename token (ASCII-safe). */
export function proposalPdfContentDispositionFilename(proposalRef: string | null | undefined): string {
  return proposalPdfDownloadFilename(proposalRef).replace(/"/g, "");
}

/**
 * Same-origin API path for the server pdfkit quotation PDF.
 * `hideItemPricing` is a render-only override (staff); it does not persist to the proposal.
 */
export function proposalQuotationPdfApiPath(
  proposalId: number,
  options?: { download?: boolean; hideItemPricing?: boolean; linkBToken?: string | null },
): string {
  const id = Math.floor(Number(proposalId));
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("Invalid proposal id for quotation PDF path.");
  }
  const params = new URLSearchParams();
  if (options?.download) params.set("download", "1");
  if (typeof options?.hideItemPricing === "boolean") {
    params.set("hideItemPricing", options.hideItemPricing ? "1" : "0");
  }
  const token = options?.linkBToken?.trim();
  if (token) params.set("token", token);
  const q = params.toString();
  return `/api/proposals/${id}/quotation.pdf${q ? `?${q}` : ""}`;
}

/**
 * Parse `hideItemPricing` query for quotation PDF generation.
 * Returns undefined when absent / unrecognized (use saved proposal preference).
 */
export function parseQuotationPdfHideItemPricingQuery(
  raw: unknown,
): boolean | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (value == null) return undefined;
  const s = String(value).trim().toLowerCase();
  if (s === "1" || s === "true" || s === "hide" || s === "yes") return true;
  if (s === "0" || s === "false" || s === "show" || s === "no") return false;
  return undefined;
}
